import { InfoOutlined } from '@mui/icons-material';
import { Alert, Box, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, Tooltip, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InvoiceTemplateStatusEnum } from 'src/shared/models/enums/invoiceTemplateStatus.enum';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useInvoiceTemplateSelection } from '../hooks/useInvoiceTemplateSelection';
import { useInvoiceTemplateType } from '../hooks/useInvoiceTemplateType';
import { FormatSection } from './formatSection';
import { InvoiceTemplateSelectionSummary } from './invoiceTemplateSelectionSummary';
import { SelectionWithProfilesSection } from './selectionWithProfilesSection';

type InvoiceTemplateInformationProps = {
  // Utan behörigheten INVOICE_CREATE_FOR_CUSTOMER döljs mallens interna inställningar.
  readOnly?: boolean;
  // Antalet räknas på den sparade mallen, så Räkna antal sparar först.
  saveBeforeCount: () => Promise<boolean>;
};

export const InvoiceTemplateInformation = ({ readOnly = false, saveBeforeCount }: InvoiceTemplateInformationProps) => {
  const { t } = useTranslation(['invoice']);
  const { control, watch } = useFormContext();
  const { selectionClauses, showAssociations } = useInvoiceTemplateSelection();
  const { isSwish, usesRecurringSelection } = useInvoiceTemplateType();

  // Två kolumner som i utskicksbeställningen: allt man fyller i till vänster, sammanfattningen av
  // urvalet till höger, åtskilda av en lodrät linje i stället för av färgade rutor.
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={8}>
        <Grid container mb={3} spacing={2}>
          {/* Mallens id och inställningarna nedan styr hur mallen beter sig internt och går bara
            att ändra med SuperAdmin. För kunden säger de ingenting begripligt, så de döljs i
            stället för att visas låsta. */}
          {!readOnly && (
            <Grid item xs={12} sm={3}>
              <ControlledTextField disabled name='template_id' label={t('invoice:adminInvoiceTemplatePage.invoiceTemplateNr')} />
            </Grid>
          )}
          <Grid item xs={12} sm={readOnly ? 12 : 9}>
            <ControlledTextField required name='name' label={t('invoice:adminInvoiceTemplatePage.invoiceTemplate')} />
          </Grid>
          {!readOnly && (
            <>
              <Grid item xs={12}>
                <ControlledTextField name='email' label={t('invoice:adminInvoiceTemplatePage.templateInformation.statusEmail')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '50%' }}>
                    <FormLabel id='label-hideTemplate'>{t('invoice:adminInvoiceTemplatePage.templateInformation.hideTemplate')}</FormLabel>
                    <Tooltip title={t('invoice:adminInvoiceTemplatePage.templateInformation.hideTemplateDescription')} arrow>
                      <InfoOutlined fontSize='small' sx={{ color: 'text.secondary', ml: 1 }} />
                    </Tooltip>
                  </Box>
                  <Controller
                    name='status'
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        aria-labelledby='label-hideTemplate'
                        name={field.name}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', ml: { sm: 0, md: 2 } }}
                      >
                        <FormControlLabel value={String(InvoiceTemplateStatusEnum.Hidden)} control={<Radio />} label={t('common:button.yes')} />
                        <FormControlLabel value={String(InvoiceTemplateStatusEnum.Visible)} control={<Radio />} label={t('common:button.no')} />
                      </RadioGroup>
                    )}
                  />
                </Box>
              </Grid>
              {/* Logga, adress och texten ovanför mottagaren hör till den utskrivna fakturan. */}
              {!isSwish && (
                <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <FormLabel id='label-useLocalAssLogo' sx={{ minWidth: '50%' }}>
                    {t('invoice:adminInvoiceTemplatePage.templateInformation.useLocalAssLogo')}
                  </FormLabel>
                  <Controller
                    name='useLocalAssLogo'
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        aria-labelledby='label-useLocalAssLogo'
                        name={field.name}
                        value={!!field.value}
                        // RadioGroup ger värdet som sträng – formuläret ska ha boolean.
                        onChange={(e) => field.onChange(e.target.value === 'true')}
                        sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', ml: { sm: 0, md: 2 } }}
                      >
                        <FormControlLabel value control={<Radio />} label={t('common:button.yes')} />
                        <FormControlLabel value={false} control={<Radio />} label={t('common:button.no')} />
                      </RadioGroup>
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <FormLabel id='label-showFormInfo' sx={{ minWidth: '50%' }}>
                  {t('invoice:adminInvoiceTemplatePage.templateInformation.showFormInfo')}
                </FormLabel>
                <Controller
                  name='showFormInfo'
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      aria-labelledby='label-showFormInfo'
                      name={field.name}
                      value={!!field.value}
                      // RadioGroup ger värdet som sträng – formuläret ska ha boolean.
                      onChange={(e) => field.onChange(e.target.value === 'true')}
                      sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', ml: { sm: 0, md: 2 } }}
                    >
                      <FormControlLabel value control={<Radio />} label={t('common:button.yes')} />
                      <FormControlLabel value={false} control={<Radio />} label={t('common:button.no')} />
                    </RadioGroup>
                  )}
                />
              </Grid>
              {!isSwish && (
                <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <FormLabel id='label-useLocalAssAddress' sx={{ minWidth: '50%' }}>
                    {t('invoice:adminInvoiceTemplatePage.templateInformation.useLocalAssAddress')}
                  </FormLabel>
                  <Controller
                    name='useLocalAssAddress'
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        aria-labelledby='label-useLocalAssAddress'
                        name={field.name}
                        value={!!field.value}
                        // RadioGroup ger värdet som sträng – formuläret ska ha boolean.
                        onChange={(e) => field.onChange(e.target.value === 'true')}
                        sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', ml: { sm: 0, md: 2 } }}
                      >
                        <FormControlLabel value control={<Radio />} label={t('common:button.yes')} />
                        <FormControlLabel value={false} control={<Radio />} label={t('common:button.no')} />
                      </RadioGroup>
                    )}
                  />
                </Grid>
              )}
              {!isSwish && (
                <Grid item xs={12}>
                  <ControlledTextField name='preReceiverText' label={t('invoice:adminInvoiceTemplatePage.templateInformation.preReceiverText')} />
                </Grid>
              )}
            </>
          )}
        </Grid>

        <Typography sx={{ fontWeight: 500, color: 'text.secondary', mb: 1.5 }}>
          {t('invoice:adminInvoiceTemplatePage.templateInformation.selection')}
        </Typography>
        {/*
          Urvalet är kundens eget. Utan behörigheten döljs mallens interna inställningar, men
          medlemsurvalet ska gå att ändra, annars kan kunden varken skapa eller underhålla en mall.
        */}
        <SelectionWithProfilesSection showAssociations={showAssociations} />

        {/* Formatet styr blankettmall, nollbelopp, samlingsfaktura och godkännandegränser. Det
          sätts av oss när mallen byggs och säger kunden ingenting, så det döljs i stället för
          att visas låst. */}
        {!readOnly && (
          <>
            <Typography sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}>
              {t('invoice:adminInvoiceTemplatePage.templateInformation.format')}
            </Typography>
            <FormatSection />
          </>
        )}
      </Grid>

      <Grid item xs={12} md={4}>
        <Box
          sx={{
            height: '100%',
            pl: { md: 3 },
            // Longhand så färgen inte återställs av borderLeft-shorthand inne i media-queryn.
            borderLeftWidth: { md: '1px' },
            borderLeftStyle: 'solid',
            borderLeftColor: 'divider',
          }}
        >
          <InvoiceTemplateSelectionSummary
            templateId={Number(watch('template_id')) || 0}
            clauses={selectionClauses}
            showCount={!usesRecurringSelection}
            saveBeforeCount={saveBeforeCount}
          />

          {/* Hjälpen erbjuds kunden. Vi som sätter upp mallarna behöver inte hänvisas till oss själva. */}
          {readOnly && (
            <Alert severity='warning' sx={{ mt: 3 }}>
              {t('invoice:adminInvoiceTemplatePage.templateInformation.selectionSupport')}
            </Alert>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};
